<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import ImapSettings from '../ImapSettings.vue';
import SmtpSettings from '../SmtpSettings.vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'next/textarea/TextArea.vue';
import WhatsappReauthorize from '../channels/whatsapp/Reauthorize.vue';
import WhatsAppTemplatesManager from '../components/WhatsAppTemplatesManager.vue';
import WhatsAppFlowsManager from '../components/WhatsAppFlowsManager.vue';
import WhatsAppBusinessProfileManager from '../components/WhatsAppBusinessProfileManager.vue';
import WhatsAppCommerceManager from '../components/WhatsAppCommerceManager.vue';
import WhatsAppQrCodesManager from '../components/WhatsAppQrCodesManager.vue';
import WhatsAppAnalyticsManager from '../components/WhatsAppAnalyticsManager.vue';
import WhatsAppBillingManager from '../components/WhatsAppBillingManager.vue';
import WhatsAppBlockedUsersManager from '../components/WhatsAppBlockedUsersManager.vue';
import WhatsAppMediaManager from '../components/WhatsAppMediaManager.vue';
import WhatsAppPhoneManagementManager from '../components/WhatsAppPhoneManagementManager.vue';
import { sanitizeAllowedDomains } from 'dashboard/helper/URLHelper';
import WhatsappBusinessManagementToken from './WhatsappBusinessManagementToken.vue';
import HmacSecretKey from './components/HmacSecretKey.vue';

export default {
  components: {
    SettingsFieldSection,
    SettingsToggleSection,
    SettingsAccordion,
    ImapSettings,
    SmtpSettings,
    NextButton,
    TextArea,
    WhatsappBusinessManagementToken,
    HmacSecretKey,
    WhatsappReauthorize,
    WhatsAppTemplatesManager,
    WhatsAppFlowsManager,
    WhatsAppBusinessProfileManager,
    WhatsAppCommerceManager,
    WhatsAppQrCodesManager,
    WhatsAppAnalyticsManager,
    WhatsAppBillingManager,
    WhatsAppBlockedUsersManager,
    WhatsAppMediaManager,
    WhatsAppPhoneManagementManager,
  },
  mixins: [inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      hmacMandatory: false,
      allowMobileWebview: false,
      whatsAppInboxAPIKey: '',
      isSyncingTemplates: false,
      allowedDomains: '',
      isUpdatingAllowedDomains: false,
      isSettingDefaults: false,
      markMessagesRead: false,
      typingIndicatorEnabled: false,
      whatsappConfigTab: 'channel',
    };
  },
  validations: {
    whatsAppInboxAPIKey: { required },
  },
  computed: {
    ...mapGetters({
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    showWhatsAppReconfigure() {
      return this.isEmbeddedSignupWhatsApp;
    },
    whatsappAppId() {
      return window.chatwootConfig?.whatsappAppId;
    },
    isForwardingEnabled() {
      return !!this.inbox.forwarding_enabled;
    },
    whatsappConfigTabs() {
      return [
        {
          key: 'channel',
          label: this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_CHANNEL'),
        },
        {
          key: 'profile',
          label: this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_PROFILE'),
        },
        {
          key: 'templates',
          label: this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_MANAGER_TITLE'
          ),
        },
        {
          key: 'flows',
          label: this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_MANAGER_TITLE'
          ),
        },
        {
          key: 'commerce',
          label: this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_COMMERCE'),
        },
        {
          key: 'qr',
          label: this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_QR'),
        },
        {
          key: 'analytics',
          label: this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_ANALYTICS'),
        },
        {
          key: 'media',
          label: this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_MEDIA'),
        },
        {
          key: 'phone_management',
          label: this.$t(
            'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_PHONE_MANAGEMENT'
          ),
        },
        {
          key: 'blocked',
          label: this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_BLOCKED'),
        },
        {
          key: 'billing',
          label: this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_BILLING'),
        },
      ];
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
    allowMobileWebview() {
      if (!this.isSettingDefaults) this.handleMobileWebviewFlag();
    },
    hmacMandatory() {
      if (!this.isSettingDefaults && this.isAWebWidgetInbox)
        this.handleHmacFlag();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.isSettingDefaults = true;
      this.hmacMandatory = this.inbox.hmac_mandatory || false;
      this.allowMobileWebview = (
        this.inbox.selected_feature_flags || []
      ).includes('allow_mobile_webview');
      this.allowedDomains = this.inbox.allowed_domains || '';
      this.markMessagesRead =
        this.inbox.provider_config?.mark_messages_read === true;
      this.typingIndicatorEnabled =
        this.inbox.provider_config?.typing_indicator_enabled === true;
      this.whatsappConfigTab = 'channel';
      this.$nextTick(() => {
        this.isSettingDefaults = false;
      });
    },
    handleHmacFlag() {
      this.updateInbox();
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            hmac_mandatory: this.hmacMandatory,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async handleMobileWebviewFlag() {
      try {
        const currentFlags = this.inbox.selected_feature_flags || [];
        const selectedFlags = this.allowMobileWebview
          ? [...currentFlags, 'allow_mobile_webview']
          : currentFlags.filter(f => f !== 'allow_mobile_webview');

        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            selected_feature_flags: selectedFlags,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateAllowedDomains() {
      this.isUpdatingAllowedDomains = true;
      const sanitizedAllowedDomains = sanitizeAllowedDomains(
        this.allowedDomains
      );
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            allowed_domains: sanitizedAllowedDomains,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.allowedDomains = sanitizedAllowedDomains;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdatingAllowedDomains = false;
      }
    },
    async updateWhatsAppInboxAPIKey() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {},
        };

        payload.channel.provider_config = {
          ...this.inbox.provider_config,
          api_key: this.whatsAppInboxAPIKey,
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async updateWhatsAppProviderConfig(config) {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            provider_config: {
              ...this.inbox.provider_config,
              ...config,
            },
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async handleMarkMessagesReadFlag() {
      if (this.isSettingDefaults) return;

      await this.updateWhatsAppProviderConfig({
        mark_messages_read: this.markMessagesRead,
      });
    },
    async handleTypingIndicatorFlag() {
      if (this.isSettingDefaults) return;

      await this.updateWhatsAppProviderConfig({
        typing_indicator_enabled: this.typingIndicatorEnabled,
      });
    },
    async handleReconfigure() {
      if (this.$refs.whatsappReauth) {
        await this.$refs.whatsappReauth.requestAuthorization();
      }
    },
    async syncTemplates() {
      this.isSyncingTemplates = true;
      try {
        await this.$store.dispatch('inboxes/syncTemplates', this.inbox.id);
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUCCESS')
        );
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSyncingTemplates = false;
      }
    },
  },
};
</script>

<template>
  <div v-if="isATwilioChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.TWILIO.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsFieldSection>
    <SettingsFieldSection
      v-if="isATwilioWhatsAppChannel"
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
      :help-text="
        $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
      "
    >
      <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON') }}
      </NextButton>
    </SettingsFieldSection>
  </div>

  <div v-else-if="isALineChannel">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.TITLE')"
      :help-text="$t('INBOX_MGMT.ADD.LINE_CHANNEL.API_CALLBACK.SUBTITLE')"
    >
      <woot-code :script="inbox.callback_webhook_url" lang="html" />
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAWebWidgetInbox">
    <div class="space-y-4">
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <TextArea
            v-model="allowedDomains"
            :placeholder="
              $t('INBOX_MGMT.SETTINGS_POPUP.ALLOWED_DOMAINS.PLACEHOLDER')
            "
            auto-height
            resize
            class="w-full [&>div]:!bg-transparent [&>div]:!border-none [&>div]:!border-0 [&>div]:px-0 [&>div]:pb-0 [&>div]:pt-0"
          />
          <div class="mt-3 flex justify-end">
            <NextButton
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="isUpdatingAllowedDomains"
              @click="updateAllowedDomains"
            />
          </div>
        </template>
      </SettingsToggleSection>
      <SettingsToggleSection
        v-model="allowMobileWebview"
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.LABEL')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.ALLOW_MOBILE_WEBVIEW.SUBTITLE')
        "
      />
    </div>

    <SettingsAccordion
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
      class="mt-6"
    >
      <SettingsToggleSection
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.TITLE')"
        :description="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.DESCRIPTION')
        "
        hide-toggle
      >
        <template #editor>
          <p class="mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.SECRET_KEY') }}
          </p>
          <HmacSecretKey :inbox="inbox" />
          <p class="mt-1.5 text-label-small text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION') }}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.chatmigo.com/docs/product/channels/live-chat/sdk/identity-validation/"
              class="text-n-blue-11 hover:underline text-label-small"
            >
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.VIEW_DOCS')
              }}
            </a>
          </p>
        </template>
      </SettingsToggleSection>

      <SettingsToggleSection
        v-model="hmacMandatory"
        :header="
          $t('INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_LABEL')
        "
        :description="
          $t(
            'INBOX_MGMT.SETTINGS_POPUP.IDENTITY_VALIDATION.REQUIRE_DESCRIPTION'
          )
        "
      />
    </SettingsAccordion>
  </div>
  <div v-else-if="isAPIInbox">
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_IDENTIFIER_SUB_TEXT')"
    >
      <woot-code :script="inbox.inbox_identifier" />
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_DESCRIPTION')"
    >
      <HmacSecretKey :inbox="inbox" />
    </SettingsFieldSection>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_VERIFICATION')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.HMAC_MANDATORY_DESCRIPTION')"
    >
      <div class="flex gap-2 items-center">
        <input
          id="hmacMandatory"
          v-model="hmacMandatory"
          type="checkbox"
          @change="handleHmacFlag"
        />
        <label for="hmacMandatory" class="text-body-main text-n-slate-12">
          {{ $t('INBOX_MGMT.EDIT.ENABLE_HMAC.LABEL') }}
        </label>
      </div>
    </SettingsFieldSection>
  </div>
  <div v-else-if="isAnEmailChannel">
    <div>
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_TITLE')"
        :help-text="
          isForwardingEnabled
            ? $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_SUB_TEXT')
            : ''
        "
      >
        <woot-code
          v-if="isForwardingEnabled"
          :script="inbox.forward_to_email"
        />
        <div
          v-else
          class="py-2 px-3 bg-n-amber-3 outline-n-amber-4 text-n-amber-11 outline outline-1 -outline-offset-1 rounded-xl"
        >
          <p class="text-body-para mb-0">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.FORWARD_EMAIL_NOT_CONFIGURED') }}
          </p>
        </div>
      </SettingsFieldSection>
    </div>
    <ImapSettings :inbox="inbox" />
    <SmtpSettings :inbox="inbox" />
  </div>
  <div v-else-if="isAWhatsAppChannel && !isATwilioChannel">
    <div v-if="inbox.provider_config" class="mt-6 grid gap-4">
      <div class="flex flex-wrap gap-2">
        <button
          v-for="tab in whatsappConfigTabs"
          :key="tab.key"
          type="button"
          class="rounded-full border px-3 py-1.5 text-sm font-medium transition-colors"
          :class="
            whatsappConfigTab === tab.key
              ? 'border-n-brand bg-n-brand/10 text-n-brand'
              : 'border-n-container bg-n-background text-n-slate-11 hover:border-n-brand'
          "
          @click="whatsappConfigTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>

      <SettingsFieldSection
        v-if="whatsappConfigTab === 'channel'"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_CHANNEL')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_CHANNEL_SETTINGS_SUBHEADER')
        "
      >
        <div class="grid gap-4">
          <template v-if="isEmbeddedSignupWhatsApp">
            <SettingsFieldSection
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
              :help-text="
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
              "
            >
              <woot-code :script="inbox.provider_config.webhook_verify_token" />
            </SettingsFieldSection>

            <SettingsFieldSection
              v-if="showWhatsAppReconfigure && whatsappAppId"
              :label="
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_TITLE')
              "
              :help-text="`${$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_SUBHEADER')} ${$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_EMBEDDED_SIGNUP_DESCRIPTION')}`"
            >
              <div class="flex flex-col gap-2 items-start">
                <NextButton @click="handleReconfigure">
                  {{
                    $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_RECONFIGURE_BUTTON')
                  }}
                </NextButton>
                <WhatsappBusinessManagementToken
                  v-if="
                    isOnChatwootCloud &&
                    inbox.provider === 'whatsapp_cloud' &&
                    isEmbeddedSignupWhatsApp
                  "
                  :inbox="inbox"
                />
              </div>
            </SettingsFieldSection>
          </template>

          <template v-else>
            <SettingsFieldSection
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_TITLE')"
              :help-text="
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_WEBHOOK_SUBHEADER')
              "
            >
              <woot-code :script="inbox.provider_config.webhook_verify_token" />
            </SettingsFieldSection>
            <SettingsFieldSection
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_TITLE')"
              :help-text="
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_SUBHEADER')
              "
            >
              <woot-code :script="inbox.provider_config.api_key" />
            </SettingsFieldSection>
            <SettingsFieldSection
              :label="
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_TITLE')
              "
              :help-text="
                $t(
                  'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_SUBHEADER'
                )
              "
            >
              <div
                class="flex flex-1 justify-between items-center whatsapp-settings--content"
              >
                <woot-input
                  v-model="whatsAppInboxAPIKey"
                  type="text"
                  class="flex-1 mr-2 [&>input]:!mb-0"
                  :placeholder="
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_PLACEHOLDER'
                    )
                  "
                />
                <NextButton
                  :disabled="v$.whatsAppInboxAPIKey.$invalid"
                  @click="updateWhatsAppInboxAPIKey"
                >
                  {{
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_SECTION_UPDATE_BUTTON'
                    )
                  }}
                </NextButton>
              </div>
            </SettingsFieldSection>
          </template>

          <SettingsFieldSection
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_TITLE')"
            :help-text="
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_SUBHEADER')
            "
          >
            <NextButton :disabled="isSyncingTemplates" @click="syncTemplates">
              {{
                $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATES_SYNC_BUTTON')
              }}
            </NextButton>
          </SettingsFieldSection>

          <SettingsToggleSection
            v-model="markMessagesRead"
            :header="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MARK_READ_LABEL')"
            :description="
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MARK_READ_SUBHEADER')
            "
            @update:model-value="handleMarkMessagesReadFlag"
          />

          <SettingsToggleSection
            v-model="typingIndicatorEnabled"
            :header="
              $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TYPING_INDICATOR_LABEL')
            "
            :description="
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TYPING_INDICATOR_SUBHEADER'
              )
            "
            @update:model-value="handleTypingIndicatorFlag"
          />
        </div>
      </SettingsFieldSection>

      <SettingsFieldSection
        v-else-if="whatsappConfigTab === 'profile'"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_PROFILE')"
        :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PROFILE_SUBHEADER')"
      >
        <WhatsAppBusinessProfileManager :inbox="inbox" :active="true" />
      </SettingsFieldSection>

      <SettingsFieldSection
        v-else-if="whatsappConfigTab === 'templates'"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_MANAGER_TITLE')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TEMPLATE_MANAGER_SUBHEADER')
        "
      >
        <WhatsAppTemplatesManager :inbox="inbox" :active="true" />
      </SettingsFieldSection>

      <SettingsFieldSection
        v-else-if="whatsappConfigTab === 'flows'"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_MANAGER_TITLE')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_FLOW_MANAGER_SUBHEADER')
        "
      >
        <WhatsAppFlowsManager :inbox="inbox" :active="true" />
      </SettingsFieldSection>

      <SettingsFieldSection
        v-else-if="whatsappConfigTab === 'commerce'"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_COMMERCE')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_COMMERCE_SUBHEADER')
        "
      >
        <WhatsAppCommerceManager :inbox="inbox" :active="true" />
      </SettingsFieldSection>

      <SettingsFieldSection
        v-else-if="whatsappConfigTab === 'qr'"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_QR')"
        :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_QR_SUBHEADER')"
      >
        <WhatsAppQrCodesManager :inbox="inbox" :active="true" />
      </SettingsFieldSection>

      <SettingsFieldSection
        v-else-if="whatsappConfigTab === 'analytics'"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_ANALYTICS')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_ANALYTICS_SUBHEADER')
        "
      >
        <WhatsAppAnalyticsManager :inbox="inbox" :active="true" />
      </SettingsFieldSection>

      <SettingsFieldSection
        v-else-if="whatsappConfigTab === 'media'"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_MEDIA')"
        :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MEDIA_SUBHEADER')"
      >
        <WhatsAppMediaManager :inbox="inbox" :active="true" />
      </SettingsFieldSection>

      <SettingsFieldSection
        v-else-if="whatsappConfigTab === 'phone_management'"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_PHONE_MANAGEMENT')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_PHONE_MANAGEMENT_SUBHEADER')
        "
      >
        <WhatsAppPhoneManagementManager :inbox="inbox" :active="true" />
      </SettingsFieldSection>

      <SettingsFieldSection
        v-else-if="whatsappConfigTab === 'blocked'"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_BLOCKED')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BLOCKED_USERS_SUBHEADER')
        "
      >
        <WhatsAppBlockedUsersManager :inbox="inbox" :active="true" />
      </SettingsFieldSection>

      <SettingsFieldSection
        v-else
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_TAB_BILLING')"
        :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_BILLING_SUBHEADER')"
      >
        <WhatsAppBillingManager :inbox="inbox" :active="true" />
      </SettingsFieldSection>
    </div>
    <WhatsappReauthorize
      v-if="isEmbeddedSignupWhatsApp"
      ref="whatsappReauth"
      :inbox="inbox"
      class="hidden"
    />
  </div>
</template>

<style lang="scss" scoped>
.whatsapp-settings--content {
  :deep(input) {
    margin-bottom: 0;
  }
}
</style>
